import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Search, Play } from "lucide-react";
import { api, fileUrl, youtubeId } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import ProductCard, { Stars } from "@/components/ProductCard";
import useSeo from "@/lib/useSeo";


function Hero() {
  const { site } = useApp();
  const slides = site?.slides || [];
  const [i, setI] = useState(0);
  const n = slides.length || 1;
  useEffect(() => { const id = setInterval(() => setI((x) => (x + 1) % n), 6000); return () => clearInterval(id); }, [n]);
  const cur = slides[i % n];
  return (
    <section className="relative h-[72vh] min-h-[480px] overflow-hidden bg-[#3B220C]" data-testid="hero-carousel">
      {slides.map((s, k) => (
        <img key={k} src={fileUrl(s.image)} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${k === i % n ? "opacity-100" : "opacity-0"}`} />
      ))}
      <div className="absolute inset-0 bg-[#1A1412]/45" />
      {cur && (
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col justify-end pb-20 text-white">
          {cur.kicker && <p key={`k${i}`} className="rise font-mono text-xs tracking-[0.25em] text-[#E8C9A6] mb-4">{cur.kicker.toUpperCase()}</p>}
          <h1 key={`h${i}`} className="rise font-serif text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[1.05]" data-testid="hero-title">{cur.title}</h1>
          {cur.cta_label && (
            <div className="rise flex flex-wrap gap-3 mt-8" style={{ animationDelay: "150ms" }}>
              <Link to={cur.cta_link || "/shop"} data-testid="hero-explore-btn" className="bg-white text-[#3B220C] px-7 py-3 text-sm tracking-wider hover:bg-[#F6F2EE] transition-colors">{cur.cta_label}</Link>
            </div>
          )}
        </div>
      )}
      <button onClick={() => setI((i + n - 1) % n)} data-testid="hero-prev-btn" aria-label="Previous" className="absolute left-4 top-1/2 z-10 text-white/80 hover:text-white"><ChevronLeft size={40} strokeWidth={1} /></button>
      <button onClick={() => setI((i + 1) % n)} data-testid="hero-next-btn" aria-label="Next" className="absolute right-4 top-1/2 z-10 text-white/80 hover:text-white"><ChevronRight size={40} strokeWidth={1} /></button>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {slides.map((_, k) => <button key={k} onClick={() => setI(k)} aria-label={`Slide ${k + 1}`} data-testid={`hero-dot-${k}`} className={`h-[2px] transition-all ${k === i % n ? "w-12 bg-white" : "w-6 bg-white/40"}`} />)}
      </div>
    </section>
  );
}


const SectionHead = ({ title, to, label }) => (
  <div className="flex items-end justify-between mb-10">
    <h2 className="font-serif italic text-3xl sm:text-4xl">{title}</h2>
    {to && <Link to={to} className="text-sm flex items-center gap-2 text-[#8C4F27] hover:gap-3 transition-all">{label}<ArrowRight size={14} /></Link>}
  </div>
);

function Collections() {
  const { t, pick } = useApp();
  const [cols, setCols] = useState([]);
  useEffect(() => { api.get("/collections").then((r) => setCols(r.data)); }, []);
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-20">
      <SectionHead title={t("collection")} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {cols.map((c, i) => (
          <Link key={c.slug} to={c.slug === "workshop" ? "/shop?type=course,workshop" : `/shop?category=${c.slug}`} data-testid={`collection-tile-${c.slug}`}
            className="group relative aspect-square overflow-hidden rise" style={{ animationDelay: `${i * 80}ms` }}>
            <img src={fileUrl(c.image)} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-[#1A1412]/40 group-hover:bg-[#1A1412]/55 transition-colors" />
            {c.coming_soon && <span className="absolute top-4 left-4 bg-[#3B220C] text-white text-[10px] font-mono tracking-[0.2em] px-2 py-1">{t("coming_soon").toUpperCase()}</span>}
            <h3 className="absolute inset-0 grid place-items-center text-center text-white font-serif text-2xl sm:text-3xl px-4 leading-tight">{pick(c, "title")}</h3>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HomeSearch() {
  const { t } = useApp();
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <form onSubmit={(e) => { e.preventDefault(); nav(`/shop?q=${encodeURIComponent(q)}`); }} className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-10">
      <div className="flex items-center border border-[#E6DFD5] bg-white px-5 focus-within:border-[#8C4F27] transition-colors">
        <Search size={18} className="text-[#6E6259]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} data-testid="home-search-input" className="flex-1 py-4 px-3 outline-none bg-transparent" />
      </div>
    </form>
  );
}

function Tutorials({ vlogs }) {
  const { t } = useApp();
  if (!vlogs.length) return null;
  const v = vlogs[0];
  const yt = youtubeId(v.video_url);
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24 grid lg:grid-cols-2 gap-10 items-center" data-testid="tutorial-section">
      <div className="space-y-4">
        <h2 className="font-serif italic text-3xl sm:text-4xl" data-testid="tutorial-title">{t("tutorial")}</h2>
        <Link to={`/journal/${v.slug}`} className="font-serif text-2xl block hover:text-[#8C4F27] transition-colors">{v.title}</Link>
      </div>
      <div className="aspect-video bg-[#1A1412] relative overflow-hidden">
        {yt ? <iframe title={v.title} className="w-full h-full" src={`https://www.youtube.com/embed/${yt}`} allowFullScreen /> : (
          <Link to={`/journal/${v.slug}`} className="group block w-full h-full">
            <img src={fileUrl(v.cover)} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
            <Play className="absolute inset-0 m-auto text-white" size={56} strokeWidth={1} />
          </Link>
        )}
      </div>
    </section>
  );
}

export const PostCard = ({ p, i }) => (
  <Link to={`/journal/${p.slug}`} data-testid={`post-card-${p.slug}`} className="group block rise" style={{ animationDelay: `${i * 80}ms` }}>
    <div className="aspect-[16/10] overflow-hidden bg-[#F6F2EE] relative">
      <img src={fileUrl(p.cover)} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      {p.kind === "vlog" && <Play className="absolute top-4 right-4 text-white" size={22} />}
    </div>
    <p className="text-sm text-[#6E6259] mt-4">{new Date(p.published_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })}</p>
    <h3 className="font-semibold text-xl mt-1 group-hover:text-[#8C4F27] transition-colors">{p.title}</h3>
    <p className="text-[#6E6259] mt-2 line-clamp-2">{p.excerpt}</p>
  </Link>
);

export default function Home() {
  const { t } = useApp();
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);
  const [reviews, setReviews] = useState([]);
  useSeo({ description: "Premium handmade leathercraft, digital leather patterns and leathercraft academy from Indonesia." });
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/posts").then((r) => setPosts(r.data));
    api.get("/reviews/latest").then((r) => setReviews(r.data));
  }, []);
  const blogs = posts.filter((p) => p.kind === "blog").slice(0, 3);
  return (
    <div data-testid="home-page">
      <Hero />
      <Collections />
      <HomeSearch />
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24">
        <SectionHead title={t("featured")} to="/shop" label={t("view_all")} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12">{featured.map((p, i) => <ProductCard key={p.product_id} p={p} i={i} />)}</div>
      </section>
      <Tutorials vlogs={posts.filter((p) => p.kind === "vlog")} />
      {reviews.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24" data-testid="reviews-section">
          <SectionHead title={t("reviews")} />
          <div className="grid md:grid-cols-3 gap-5">
            {reviews.slice(0, 3).map((r) => (
              <figure key={r.review_id} className="bg-white border border-[#E6DFD5] p-8 space-y-4">
                <Stars value={r.rating} />
                <blockquote className="font-serif text-xl leading-snug">"{r.comment}"</blockquote>
                <figcaption className="text-sm text-[#6E6259]">{r.user_name} · <Link to={`/product/${r.product_slug}`} className="underline">{r.product_name}</Link></figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-24">
        <SectionHead title={t("home_blog")} to="/journal" label={t("view_all")} />
        <div className="grid md:grid-cols-3 gap-6">{blogs.map((p, i) => <PostCard key={p.post_id} p={p} i={i} />)}</div>
      </section>
    </div>
  );
}
